import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { StyledContainer } from '../../dynamicform/styled.js';
import { validateAuth, getApiError, formatLocalTimestamp } from '../../util/util.js';
import { notificationError } from '../../redux/actions/app.js';

// Keys mirror PortalAccess::UserQuery::PORTAL_STATUSES in datacentre-api
const PORTAL_STATUS_LABELS = {
  not_invited: 'No Account',
  pending: 'Not Activated',
  invite_expired: 'Invite Expired',
  active: 'Active',
  disabled: 'Disabled',
  reset_required: 'Reset Required',
  unverified: 'Unverified',
  compromised: 'Compromised',
  unknown: 'Unknown',
};

const statusLabel = (key) =>
  PORTAL_STATUS_LABELS[key] ??
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Mirrors PortalAccess::UserQuery.action_for
const actionFor = (portalStatus) => {
  if (portalStatus === 'not_invited') return 'invite';
  if (portalStatus === 'pending' || portalStatus === 'invite_expired') return 'resend';
  return 'reset';
};

const ACTION_BUTTON_LABEL = { invite: 'Send', resend: 'Resend', reset: 'Reset & Send' };

const OUTCOMES_BY_OPERATION = {
  welcome_email: ['sent', 'resent', 'reset', 'skipped', 'failed'],
  remove: ['removed', 'skipped', 'failed'],
};
const ACTIVE_BATCH_STATUSES = ['queued', 'running'];
const POLL_MS = 3000;
const RECENT_HOURS = 24;

const TEXT_FILTERS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  // { key: 'datacentre', label: 'Datacentre' },
  // { key: 'customer', label: 'Customer' },
];

const EMPTY_FILTERS = {
  name: '',
  email: '',
  // datacentre: '',
  // customer: '',
  statuses: [],
  welcomeEmail: '',
};

const buildParams = (filters) => {
  const params = {};
  TEXT_FILTERS.forEach(({ key }) => {
    if (filters[key]) params[key] = filters[key];
  });
  if (filters.statuses.length > 0) params.portal_status = filters.statuses.join(',');
  if (filters.welcomeEmail) params.welcome_email = filters.welcomeEmail;
  return params;
};

const portalAccessApi = (apiBase, { path, timeout = 30000, ...config }) =>
  validateAuth(async (session) => {
    const response = await axios({
      ...config,
      url: `${apiBase}/portal_access/${path}`,
      headers: { Authorization: session.getIdToken().getJwtToken() },
      timeout,
    });
    return response.data;
  });

const formatCounts = (counts, keys) =>
  keys.map((key) => `${key}: ${counts?.[key] ?? 0}`).join(' · ');

// Outcomes for the batch's operation, plus any other outcome that is non-zero
const batchCountKeys = (batch) => {
  const keys = OUTCOMES_BY_OPERATION[batch.operation] ?? OUTCOMES_BY_OPERATION.welcome_email;
  const extras = Object.keys(batch.counts || {}).filter(
    (key) => !keys.includes(key) && batch.counts[key] > 0
  );
  return [...keys, ...extras];
};

const sumActions = (actions) =>
  (actions?.invite ?? 0) + (actions?.resend ?? 0) + (actions?.reset ?? 0);

function LastSent({ info }) {
  const lastError = info?.last_error && (
    <Tooltip
      title={`${formatLocalTimestamp(info.last_error.attempted_at)}: ${info.last_error.reason}`}
    >
      <Typography variant="caption" sx={{ display: 'block', textDecoration: 'underline dotted' }}>
        Last attempt failed
      </Typography>
    </Tooltip>
  );
  if (!info?.last_sent_at) {
    return (
      <>
        Never
        {lastError}
      </>
    );
  }
  return (
    <>
      {formatLocalTimestamp(info.last_sent_at)}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {info.last_sent_by ?? '—'} · {info.send_count ?? 1}x
      </Typography>
      {lastError}
    </>
  );
}

// Pass `selection` + `operation` to preview and start a batch, or `batchId` to monitor one.
function PortalAccessBatchDialog({
  selection,
  operation,
  batchId: initialBatchId,
  apiBase,
  onClose,
  onFinished,
}) {
  const dispatch = useDispatch();
  const isRemove = operation === 'remove';
  const [preview, setPreview] = useState(null);
  const [skipRecent, setSkipRecent] = useState(true);
  const [starting, setStarting] = useState(false);
  const [batchId, setBatchId] = useState(initialBatchId ?? null);
  const [batch, setBatch] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selection) return;
    portalAccessApi(apiBase, { method: 'post', path: 'welcome_email_preview', data: selection })
      .then(setPreview)
      .catch((e) => setError(getApiError(e, 'Preview failed').message));
  }, []);

  useEffect(() => {
    if (!batchId) return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const data = await portalAccessApi(apiBase, {
          method: 'get',
          path: `welcome_email_batches/${batchId}`,
        });
        if (stopped) return;
        setBatch(data.batch);
        if (ACTIVE_BATCH_STATUSES.includes(data.batch?.status)) timer = setTimeout(poll, POLL_MS);
        else if (data.batch) onFinished(data.batch);
      } catch (e) {
        if (!stopped) setError(getApiError(e, 'Failed to load batch').message);
      }
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [batchId]);

  const isActive = ACTIVE_BATCH_STATUSES.includes(batch?.status);

  const handleStart = async () => {
    setStarting(true);
    try {
      const data = await portalAccessApi(apiBase, {
        method: 'post',
        path: isRemove ? 'remove_users' : 'send_welcome_email',
        data: isRemove ? selection : { ...selection, skip_recently_sent: skipRecent },
        timeout: 60000,
      });
      setBatch(data.batch);
      setBatchId(data.batch.id);
    } catch (e) {
      setError(getApiError(e, 'Failed to start batch').message);
    } finally {
      setStarting(false);
    }
  };

  const handleCancelBatch = async () => {
    setCancelling(true);
    try {
      const data = await portalAccessApi(apiBase, {
        method: 'post',
        path: `welcome_email_batches/${batchId}/cancel`,
      });
      setBatch(data.batch);
    } catch (e) {
      dispatch(notificationError(getApiError(e, 'Failed to stop batch').message));
    } finally {
      setCancelling(false);
    }
  };

  const handleClose = () => {
    if (starting) return;
    onClose(batch);
  };

  if (batchId) {
    const problems = batch?.problems || [];
    const problemCount = (batch?.counts?.skipped ?? 0) + (batch?.counts?.failed ?? 0);
    const batchIsRemove = (batch?.operation ?? operation) === 'remove';
    let title = `${batchIsRemove ? 'Remove users' : 'Welcome email'} batch #${batchId}`;
    if (isActive) title = batchIsRemove ? 'Removing users…' : 'Sending welcome emails…';
    return (
      <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!batch ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography variant="body2">
                {batch.status}
                {batch.cancel_requested && isActive ? ' (stopping)' : ''} · {batch.processed}/
                {batch.total} · by {batch.created_by ?? '—'} ·{' '}
                {formatLocalTimestamp(batch.created_at)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={batch.total ? (batch.processed / batch.total) * 100 : 0}
              />
              <Typography variant="body2">
                {formatCounts(batch.counts, batchCountKeys(batch))}
              </Typography>
              {problems.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    skipped/failed: showing {problems.length} of {problemCount}
                  </Typography>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Outcome</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {problems.map((r) => (
                          <TableRow key={r.contact_id}>
                            <TableCell>{r.email ?? '—'}</TableCell>
                            <TableCell>{r.outcome}</TableCell>
                            <TableCell>{r.reason ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isActive && (
            <Button
              color="error"
              onClick={handleCancelBatch}
              disabled={cancelling || batch?.cancel_requested}
            >
              Stop
            </Button>
          )}
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (isRemove) {
    const removeCount = preview?.total ?? 0;
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Remove from App/Portal</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!preview && !error && <CircularProgress size={24} />}
          {preview && (
            <Typography variant="body2">
              Remove App/Portal access for <strong>{removeCount}</strong> user(s)? Their portal
              accounts are deleted, they are signed out, and &apos;App/Portal user&apos; is turned
              off for the contact. They will no longer appear on this screen.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={starting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleStart}
            disabled={!preview || starting || removeCount === 0}
          >
            {starting ? 'Starting…' : `Remove ${removeCount}`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const actions = skipRecent ? preview?.actions_not_recent : preview?.actions;
  const toSendCount = sumActions(actions);

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Welcome Email</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!preview && !error && <CircularProgress size={24} />}
        {preview && (
          <>
            <Typography variant="body2">
              AWS will email each user a temporary password for the Data Centre Portal. Sending runs
              in the background and can be stopped at any time.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <Typography variant="body2">
                  <strong>{actions?.invite ?? 0}</strong> new invite(s): a portal account will be
                  created.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{actions?.resend ?? 0}</strong> resend(s): a new temporary password
                  replaces the previous one, which stops working.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{actions?.reset ?? 0}</strong> reset(s): existing accounts are recreated
                  with a new temporary password.
                </Typography>
              </li>
            </Box>
            {(actions?.reset ?? 0) > 0 && (
              <Alert severity="warning">
                {actions.reset} user(s) already have a portal account. They will be signed out of
                the portal and app, their current password will stop working, and any authenticator
                app (MFA) setup must be redone. Filter out <strong>Active</strong> users if you
                don&apos;t want this.
              </Alert>
            )}
            {preview.recently_sent > 0 && (
              <Alert severity="warning">
                {preview.recently_sent} of the selected user(s) were emailed in the last{' '}
                {RECENT_HOURS} hours.
                <FormControlLabel
                  sx={{ display: 'flex', mt: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={skipRecent}
                      disabled={starting}
                      onChange={(e) => setSkipRecent(e.target.checked)}
                    />
                  }
                  label={`Skip users emailed in the last ${RECENT_HOURS} hours`}
                />
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={starting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleStart}
          disabled={!preview || starting || toSendCount === 0}
        >
          {starting ? 'Starting…' : `Send to ${toSendCount}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PortalAccessTab() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const apiBase = process.env.REACT_APP_AWS_API_URL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // When selectAll is true, selectedIds holds the exclusions instead of the inclusions
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [dialog, setDialog] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);

  // Any status the backend returns that isn't in the known list still gets a filter option
  const statusOptions = [
    ...Object.keys(PORTAL_STATUS_LABELS),
    ...Object.keys(statusCounts).filter((key) => !(key in PORTAL_STATUS_LABELS)),
  ];

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await portalAccessApi(apiBase, {
        method: 'get',
        path: 'get_users',
        params: { ...buildParams(applied), page: page + 1, per_page: rowsPerPage },
      });
      setRows(data?.data || []);
      setTotal(data?.total ?? 0);
      setStatusCounts(data?.status_counts || {});
    } catch (error) {
      setRows([]);
      setTotal(0);
      dispatch(notificationError(getApiError(error, 'Failed to load portal users').message));
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectAll(false);
    setSelectedIds(new Set());
  };

  const handleBatchFinished = () => {
    clearSelection();
    setReloadKey((k) => k + 1);
  };

  const fetchActiveBatch = async () => {
    try {
      const data = await portalAccessApi(apiBase, {
        method: 'get',
        path: 'welcome_email_batches/current',
      });
      const next = data?.batch ?? null;
      if (activeBatch && next?.id !== activeBatch.id) handleBatchFinished();
      setActiveBatch(next);
    } catch {
      setActiveBatch(null);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [page, rowsPerPage, applied, reloadKey]);

  useEffect(() => {
    fetchActiveBatch();
  }, []);

  const isBatchActive = ACTIVE_BATCH_STATUSES.includes(activeBatch?.status);

  // The dialog polls while open; otherwise poll here so a background batch's finish reloads the list
  useEffect(() => {
    if (!isBatchActive || dialog) return undefined;
    const timer = setTimeout(fetchActiveBatch, POLL_MS);
    return () => clearTimeout(timer);
  }, [activeBatch, dialog]);

  useEffect(() => {
    clearSelection();
  }, [applied]);

  const applyFilters = (next) => {
    setDraft(next);
    setApplied(next);
    setPage(0);
  };

  const selectedCount = selectAll ? Math.max(total - selectedIds.size, 0) : selectedIds.size;
  const allChecked = selectedCount > 0 && selectedCount === total;
  const isRowSelected = (id) => selectAll !== selectedIds.has(id);

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) {
      clearSelection();
    } else {
      setSelectAll(true);
      setSelectedIds(new Set());
    }
  };

  const selectionPayload = () =>
    selectAll
      ? { select_all: true, filters: buildParams(applied), excluded_ids: [...selectedIds] }
      : { contact_ids: [...selectedIds] };

  const handleDialogClose = () => {
    setDialog(null);
    fetchActiveBatch();
  };

  return (
    <Box sx={{ p: theme.spacing(3) }}>
      <StyledContainer>
        <Typography variant="overline">Portal Access (app_user contacts)</Typography>

        {isBatchActive && (
          <Typography variant="body2" sx={{ my: 1 }}>
            Batch #{activeBatch.id} (
            {activeBatch.operation === 'remove' ? 'remove' : 'welcome email'}) {activeBatch.status}:{' '}
            {activeBatch.processed}/{activeBatch.total} by {activeBatch.created_by ?? '—'}{' '}
            <Button size="small" onClick={() => setDialog({ batchId: activeBatch.id })}>
              View
            </Button>
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr) auto' },
            gap: 1,
            my: 2,
          }}
        >
          {TEXT_FILTERS.map(({ key, label }) => (
            <TextField
              key={key}
              label={label}
              value={draft[key]}
              onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters(draft)}
              size="small"
            />
          ))}
          <FormControl size="small">
            <InputLabel id="portal-status-filter-label">Portal Status</InputLabel>
            <Select
              labelId="portal-status-filter-label"
              label="Portal Status"
              multiple
              value={draft.statuses}
              onChange={(e) => applyFilters({ ...draft, statuses: e.target.value })}
              renderValue={(value) => value.map(statusLabel).join(', ')}
            >
              {statusOptions.map((key) => (
                <MenuItem key={key} value={key} dense>
                  <Checkbox size="small" checked={draft.statuses.includes(key)} />
                  {statusLabel(key)} ({statusCounts[key] ?? 0})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControl size="small">
            <InputLabel id="welcome-email-filter-label">Welcome Email</InputLabel>
            <Select
              labelId="welcome-email-filter-label"
              label="Welcome Email"
              value={draft.welcomeEmail}
              onChange={(e) => applyFilters({ ...draft, welcomeEmail: e.target.value })}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="never">Never sent</MenuItem>
              <MenuItem value="sent">Sent before</MenuItem>
            </Select>
          </FormControl> */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => applyFilters(draft)} disabled={loading}>
              Search
            </Button>
            <Button size="small" onClick={() => applyFilters(EMPTY_FILTERS)} disabled={loading}>
              Clear
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2">
            {selectedCount}/{total} selected{selectAll ? ' (all matching filters)' : ''}
          </Typography>
          {selectedCount > 0 && (
            <Button size="small" onClick={clearSelection}>
              Clear selection
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={selectedCount === 0 || isBatchActive}
            onClick={() => setDialog({ selection: selectionPayload(), operation: 'welcome_email' })}
          >
            Send Welcome Email
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={selectedCount === 0 || isBatchActive}
            onClick={() => setDialog({ selection: selectionPayload(), operation: 'remove' })}
          >
            Remove Selected
          </Button>
        </Box>

        {loading && <LinearProgress />}
        <TableContainer>
          {/* Long emails and Cognito statuses wrap so the table fits without horizontal scroll */}
          <Table size="small" sx={{ '& td, & th': { overflowWrap: 'anywhere' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={selectedCount > 0 && !allChecked}
                    disabled={total === 0}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                {/* <TableCell>Datacentre</TableCell>
                <TableCell>Customer</TableCell> */}
                <TableCell>Portal Status</TableCell>
                <TableCell>Cognito</TableCell>
                <TableCell>Last Welcome Email</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>No contacts.</TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const rowSelected = isRowSelected(row.id);
                return (
                  <TableRow key={row.id} hover selected={rowSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={rowSelected}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>{row.name ?? '—'}</TableCell>
                    <TableCell>{row.email ?? '—'}</TableCell>
                    {/* <TableCell>{(row.datacentres || []).join(', ') || '—'}</TableCell>
                    <TableCell>{(row.customers || []).join(', ') || '—'}</TableCell> */}
                    <TableCell>
                      {row.portal_status ? statusLabel(row.portal_status) : '—'}
                      {row.invite_expires_at && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          expires {moment.utc(row.invite_expires_at).local().format('DD/MM/YYYY')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.status ?? '—'}</TableCell>
                    <TableCell>
                      <LastSent info={row.welcome_email} />
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isBatchActive}
                          onClick={() =>
                            setDialog({
                              selection: { contact_ids: [row.id] },
                              operation: 'welcome_email',
                            })
                          }
                        >
                          {ACTION_BUTTON_LABEL[actionFor(row.portal_status)]}
                        </Button>
                        {/* <Button
                          variant="outlined"
                          size="small"
                          disabled={isBatchActive}
                          onClick={() =>
                            setDialog({ selection: { contact_ids: [row.id] }, operation: 'remove' })
                          }
                        >
                          Remove
                        </Button> */}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </StyledContainer>

      {dialog && (
        <PortalAccessBatchDialog
          selection={dialog.selection}
          operation={dialog.operation}
          batchId={dialog.batchId}
          apiBase={apiBase}
          onClose={handleDialogClose}
          onFinished={handleBatchFinished}
        />
      )}
    </Box>
  );
}
